import {
  type InfiniteData,
  type QueryFunctionContext,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
} from '@tanstack/react-query';

import { providerService } from '@/services/provider-service';
import type { Feature } from '@/types/core/provider';
import type { Page } from '@/types/shared/page';
import type { UUID } from '@/utils/uuid';

import { providerQueryKeys } from './query-keys';

/**
 * List provider features for a given provider.
 */
export function useListProviderFeatures(
  tenantId: UUID | undefined,
  providerId: UUID | undefined,
  opts?: {
    limit?: number;
    next?: string;
    filter?: string;
    enabled?: boolean;
  }
): UseInfiniteQueryResult<InfiniteData<Page<Feature>, unknown>, Error> {
  return useInfiniteQuery<
    Page<Feature>,
    Error,
    InfiniteData<Page<Feature>, unknown>
  >({
    queryKey: providerQueryKeys.featureList(
      tenantId,
      providerId,
      opts?.limit,
      opts?.filter
    ),
    queryFn: (context: QueryFunctionContext) => {
      if (!tenantId || !providerId) {
        throw new Error('Missing tenantId or providerId');
      }
      const pageParam = context.pageParam as string | undefined;
      return providerService.listProviderFeatures(tenantId, providerId, {
        limit: opts?.limit,
        next: pageParam,
        filter: opts?.filter,
      });
    },
    enabled: opts?.enabled ?? (!!tenantId && !!providerId),
    initialPageParam: opts?.next ?? undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    getPreviousPageParam: (first) => first.prev ?? undefined,
  });
}
